import cx from 'classnames';
import {isEmpty, map} from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';
import ReactRating from 'react-rating';

import {Icon, IconName, IconSize} from 'components';
import {HorizontalDirection} from 'enums';

type Props = {
    className?: string;
    labels?: string[];
    size?: 'small' | 'medium' | 'large';
    value?: number;
    fractions?: 1 | 2;
    direction?: HorizontalDirection
    onChange?: (value: number) => void;
}

const ICON_NAMES: IconName[] = [
    'face-very-dissatisfied',
    'face-dissatisfied',
    'face-neutral',
    'face-satisfied',
    'face-very-satisfied'
];

const ACTIVE_COLOR_MAP = {
    'face-very-dissatisfied': 'FA2E2E',
    'face-dissatisfied': '#FF6B00',
    'face-neutral': '#FCCA1E',
    'face-satisfied': '#86DA1A',
    'face-very-satisfied': '#14CC62'
};

const Rating = ReactRating as any;

export const FaceRating: React.FC<Props> = ({
    labels = [], className, value = 0, fractions = 1, size = 'small', direction, onChange
}: Props) => {
    const [highlightIndex, setHighlightIndex] = useState(value - 1);
    const label = useMemo(() => labels[highlightIndex], [labels, highlightIndex]);
    const iconSize = useMemo((): IconSize => {
        if (size === 'small') {
            return 'md';
        } else if (size === 'large') {
            return '4xl';
        } else {
            return 'xl';
        }
    }, [size]);

    const iconData: {color: string, name: IconName}[] = useMemo(() =>
        map(ICON_NAMES, (name) => ({name, color: ACTIVE_COLOR_MAP[name]})), []);

    const handleMouseLeave = useCallback(() => {
        if (value) {
            setHighlightIndex(value - 1);
        } else {
            setHighlightIndex(-1);
        }
    }, [value]);

    const getIcons = useCallback(({active}: {active: boolean}) => map(iconData, ({name, color}, index) => (
        <Icon
            name={name}
            style={active && index === highlightIndex ? {color} : {}}
            color='default-dimmed'
            size={iconSize}
            fill='currentColor'
            className={cx('-sm:tw-m-1.5 sm:tw-m-2', {
                'tw-scale-125': active && index === highlightIndex && value - 1 !== highlightIndex,
                'tw-scale-110': active && index === highlightIndex && value - 1 === highlightIndex
            })}
            onMouseEnter={() => setHighlightIndex(index)}
            onMouseLeave={handleMouseLeave}
        />
    )), [iconData, iconSize, highlightIndex, value, handleMouseLeave]);

    useEffect(() => {
        setHighlightIndex(value - 1);
    }, [value]);

    return (
        <div className={cx(className, 'tw-flex tw-flex-col tw-items-center')}>
            <Rating
                start={0}
                stop={5}
                step={1}
                initialRating={value}
                fractions={fractions}
                emptySymbol={getIcons({active: false})}
                fullSymbol={getIcons({active: true})}
                readonly={!onChange}
                direction={direction}
                onClick={onChange}
            />
            {!isEmpty(labels) && <div className='tw-ts-h5 tw-mt-2 tw-min-h-6 tw-text-center'>{label}</div>}
        </div>
    );
};